import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Render a React subtree into ANOTHER window's document (ported from Relay's Interview Console,
 * RELAY-720). A Document PiP / popup window starts as a blank same-origin document with none of
 * the app's CSS, so before anything renders this copies, each in its own effect so one cannot
 * re-run another (a re-run stylesheet clone would append every sheet twice):
 *  1. the title, so the OS window has a name;
 *  2. the opener `<html>`'s `class` and `style`, MIRRORED for the window's life — the theme is the
 *     `dark` class (`applyTheme`), so a one-shot copy would leave the window on the old theme;
 *  3. every `<link rel="stylesheet">` / `<style>` of the opener's head, deduped. In development
 *     Vite injects CSS as `<style>` tags after first paint, so the head is watched as well.
 *
 * React events work in the portal (React attaches listeners to the portal container). Radix
 * portals do NOT: Dialog/Popover/Select/Tooltip portal to `document.body`, which from here is the
 * OPENER's body, so they would open in the main tab. The companion uses plain elements only.
 */
export function ExternalWindowPortal({
  win,
  title,
  children,
}: {
  win: Window | null;
  title: string;
  children: ReactNode;
}) {
  const [styled, setStyled] = useState(false);

  useEffect(() => {
    if (!win || win.closed) return;
    win.document.title = title;
  }, [win, title]);

  useEffect(() => {
    if (!win || win.closed) return;
    const src = document.documentElement;
    const dst = win.document.documentElement;
    const sync = () => {
      if (win.closed) return;
      dst.className = src.className;
      const style = src.getAttribute('style');
      if (style === null) dst.removeAttribute('style');
      else dst.setAttribute('style', style);
    };
    sync();
    win.document.body.style.margin = '0';
    const observer = new MutationObserver(sync);
    observer.observe(src, { attributes: true, attributeFilter: ['class', 'style'] });
    return () => observer.disconnect();
  }, [win]);

  useEffect(() => {
    if (!win || win.closed) {
      setStyled(false);
      return;
    }
    const doc = win.document;
    const seen = new Set<string>();
    const keyOf = (node: Element) =>
      node.tagName === 'LINK'
        ? `link:${(node as HTMLLinkElement).href}`
        : `style:${node.textContent ?? ''}`;
    const clone = (node: Element) => {
      const key = keyOf(node);
      if (seen.has(key)) return;
      seen.add(key);
      doc.head.appendChild(node.cloneNode(true));
    };
    document.head.querySelectorAll('link[rel="stylesheet"], style').forEach(clone);
    let observer: MutationObserver | null = null;
    if (import.meta.env.DEV) {
      observer = new MutationObserver((records) => {
        if (win.closed) return;
        for (const r of records)
          r.addedNodes.forEach((node) => {
            if (
              node instanceof Element &&
              (node.tagName === 'STYLE' ||
                (node.tagName === 'LINK' && (node as HTMLLinkElement).rel === 'stylesheet'))
            )
              clone(node);
          });
      });
      observer.observe(document.head, { childList: true });
    }
    setStyled(true);
    return () => observer?.disconnect();
  }, [win]);

  if (!win || win.closed || !styled) return null;
  return createPortal(
    <div className="bg-background text-foreground h-screen overflow-hidden font-sans">
      {children}
    </div>,
    win.document.body,
  );
}
